import { useEffect, useState, useCallback } from "react";
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function resolveImg(src) {
  if (!src) return src;
  return src.startsWith("/api/") ? `${BACKEND_URL}${src}` : src;
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(", ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const COLLECTIONS = [
  { key: "projects", label: "Projects" },
  { key: "blog", label: "Blog Posts" },
  { key: "events", label: "Events & Calendar" },
  { key: "publications", label: "Publications" },
  { key: "team", label: "Team" },
  { key: "legacy", label: "Legacy (Past Presidents)" },
  { key: "gallery", label: "Gallery" },
  { key: "achievements", label: "Achievements" },
];

export function useCollection(name) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${name}`);
      setItems(data || []);
      setError(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [name]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { items, loading, error, reload };
}

export async function saveItem(collection, item, id) {
  if (id) {
    const { data } = await api.put(`/${collection}/${id}`, item);
    return data;
  }
  const { data } = await api.post(`/${collection}`, item);
  return data;
}

export async function deleteItem(collection, id) {
  const { data } = await api.delete(`/${collection}/${id}`);
  return data;
}

// Fetches admin-editable page content, merged over the page's built-in defaults.
// defaults must be a stable module-level object (e.g. PAGES.home.defaults).
export function usePageContent(page, defaults) {
  const [content, setContent] = useState(defaults);
  useEffect(() => {
    let live = true;
    api
      .get(`/page-content/${page}`)
      .then(({ data }) => {
        if (!live || !data?.data) return;
        const merged = { ...defaults };
        Object.keys(defaults).forEach((k) => {
          const v = data.data[k];
          if (v === undefined || v === null) return;
          if (Array.isArray(defaults[k]) && !Array.isArray(v)) return; // type guard
          merged[k] = v;
        });
        setContent(merged);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);
  return content;
}

export function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export function fmtDateShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export const IMG = {
  leopardEyes:
    "https://static.prod-images.emergentagent.com/jobs/9abc48fe-bbf6-4679-bc98-0f1051d62c52/images/4ec551b39becff488618dd92f091324a95adc9908ce6535082db0aba087fba45.jpeg",
  leopardForest: "/leopard_forest.png",
  heroLeopard:
    "https://static.prod-images.emergentagent.com/jobs/9abc48fe-bbf6-4679-bc98-0f1051d62c52/images/b0f8a44976708389317d8bc6f5abc257767fc08790a104723a6aebd9a393867b.jpeg",
  leopardPortrait:
    "https://static.prod-images.emergentagent.com/jobs/9abc48fe-bbf6-4679-bc98-0f1051d62c52/images/52ba986e5b23115d4b72231a36171708f3312835e6a3d98aee2ed51656246e94.jpeg",
  forestMist:
    "https://static.prod-images.emergentagent.com/jobs/9abc48fe-bbf6-4679-bc98-0f1051d62c52/images/700f032e2b0e7cfbdae59c029fb3c0adfca8e105f69497863615e2323fbaeccd.jpeg",
  celestialSky:
    "https://static.prod-images.emergentagent.com/jobs/9abc48fe-bbf6-4679-bc98-0f1051d62c52/images/f0cc3b7fca416acacd19e4d0fc42c9b1aa9389d7f645a606f63fe16ffe53c5ba.jpeg",
  emberTexture:
    "https://static.prod-images.emergentagent.com/jobs/9abc48fe-bbf6-4679-bc98-0f1051d62c52/images/4b1f0f84998ad583300e31b3a88d539d56901df11866ec794ddd7391b5e72c56.jpeg",
};
